import { GRAMS_PER_TOLA, type Country } from "./gold-data";
import { getDateParts, type CountryKey } from "./dateIntent";

export const SITE_URL = "https://drhint.com";

export type SeoCity = { slug: string; name: string };

// Default social share image used as a fallback across hub / city pages
// that don't yet ship a page-specific og:image. Kept in sync with the
// image used by `buildAliasHead` in alias-routes.ts.
export const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/H8KExPuR0jRNdSZTB0HQVORkvLq1/social-images/social-1780834751164-IMG_8313.webp";

// NOTE: page-level titles/meta must stay stable so Google/Bing don't
// see the URL as changing every day. Date phrases belong inside the
// body copy (near the live rate), not in <title>, <meta description>,
// og:title, og:description or JSON-LD names / @id / dateModified.

export function hreflangLinks(path: string) {
  // path begins with "/"
  const href = `${SITE_URL}${path}`;
  // Determine country variants
  const pkAlt = path.startsWith("/india")
    ? "/pakistan"
    : path.startsWith("/city/india/")
      ? "/pakistan"
      : path;
  const inAlt = path.startsWith("/pakistan")
    ? "/india"
    : path.startsWith("/city/pakistan/")
      ? "/india"
      : path;
  const base = [
    { rel: "alternate", hreflang: "en", href },
    { rel: "alternate", hreflang: "en-pk", href: `${SITE_URL}${pkAlt}` },
    { rel: "alternate", hreflang: "en-in", href: `${SITE_URL}${inAlt}` },
    { rel: "alternate", hreflang: "x-default", href },
  ];
  // Diaspora reciprocal hreflangs — signal cross-border multi-regional
  // relevance for the PK / IN gold hubs and city pages. All variants point
  // back to the same absolute URL (reciprocal, per user directive).
  const isPK = path.startsWith("/pakistan") || path.startsWith("/city/pakistan/");
  const isIN = path.startsWith("/india") || path.startsWith("/city/india/");
  if (!isPK && !isIN) return base;
  const diaspora = isPK
    ? ["ur-PK", "en-US", "en-GB", "en-AE", "ar-AE", "en-SA", "ar-SA", "en-CA", "en-AU", "bn-BD", "en-MY"]
    : ["hi-IN", "en-US", "en-GB", "en-AE", "ar-AE", "en-SA", "ar-SA", "en-CA", "en-AU", "en-SG", "en-MY"];
  return [
    ...base,
    ...diaspora.map((hl) => ({ rel: "alternate", hreflang: hl, href })),
  ];
}

export function cityHeadMeta(country: Country, city: SeoCity) {
  const title =
    country === "pakistan"
      ? `${city.name} Gold Rate Today — 24K & 22K Live Price`
      : `${city.name} Gold Price Today — 22K & 24K Live Rate`;
  const description =
    country === "pakistan"
      ? `Live gold rate in ${city.name}, Pakistan today. 24K, 22K, 21K per tola, per gram & per 10g. Updated every 10 minutes from international spot + USD/PKR.`
      : `Today's live gold price in ${city.name}, India. 22 carat and 24 carat gold rate per 10g, per gram & per tola. Updated every 10 minutes.`;
  const path = `/city/${country}/${city.slug}`;
  return {
    title,
    description,
    path,
    canonicalHref: `${SITE_URL}${path}`,
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: `${SITE_URL}${path}` },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: country === "pakistan" ? "en_PK" : "en_IN" },
      { property: "og:image", content: DEFAULT_OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: DEFAULT_OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: `${SITE_URL}${path}` },
      ...hreflangLinks(path),
    ],
  };
}

export function countryHeadMeta(country: Country) {
  const cName = country === "pakistan" ? "Pakistan" : "India";
  const title =
    country === "pakistan"
      ? `Gold Rate in Pakistan Today — Live PKR Price for Overseas Pakistanis in KSA, UAE, US, UK`
      : `Today Gold Price in India — Live INR Rate for NRI / Global Users (Gulf, USA, UK)`;
  const description =
    country === "pakistan"
      ? `Live gold rate in Pakistan — Karachi, Lahore, Islamabad 24K, 22K, 21K, 18K per tola & gram. Pakistan me aaj sone ka rate kya hai for overseas Pakistanis in Saudi Arabia (KSA), UAE (Dubai), USA, UK & Bangladesh — SAR/AED/USD/GBP live conversion. Refreshed every 10 minutes.`
      : `India me aaj sone ka rate kya hai — live 24 carat, 22 carat, 18 carat per 10 gram, tola across Delhi, Mumbai, Chennai. आज India में सोने का भाव for NRI / global users in Gulf, USA & UK with SAR/AED/USD/GBP conversion. Refreshed every 10 minutes.`;
  const path = `/${country}`;
  return {
    title,
    description,
    path,
    cName,
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: `${SITE_URL}${path}` },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: country === "pakistan" ? "en_PK" : "en_IN" },
      { property: "og:image", content: DEFAULT_OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: DEFAULT_OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: `${SITE_URL}${path}` },
      ...hreflangLinks(path),
    ],
  };
}

/**
 * Date-intent meta builder for the 4 indexed gold hubs.
 * Used by /pakistan, /india, /uae, /ar/gold-rate-saudi-arabia.
 * Canonical & og:url remain stable.
 */
export function hubDateMeta(country: CountryKey) {
  const dp = getDateParts(country);
  let path: string;
  let title: string;
  let description: string;
  let locale: string;

  switch (country) {
    case "pakistan":
      path = "/pakistan";
      title = `Gold Rate in Pakistan Today — 24K, 22K, 21K Per Tola & Gram`;
      description = `Live gold price in Pakistan: Karachi, Lahore, Islamabad, Rawalpindi. 24K, 22K, 21K, 18K per tola & per gram. Refreshed every 10 minutes.`;
      locale = "en_PK";
      break;
    case "india":
      path = "/india";
      title = `Gold Rate in India Today — 24K, 22K Per 10 Gram (Delhi, Mumbai, Chennai)`;
      description = `Live gold rate in India: Delhi, Mumbai, Bangalore, Chennai, Kolkata. 24K, 22K per 10 gram & per gram. Refreshed every 10 minutes.`;
      locale = "en_IN";
      break;
    case "uae":
      path = "/uae";
      // Evergreen title: no calendar date (it was freezing as "4 August" in
      // SERPs and killing CTR). One long-tail head term that covers
      // gold rate uae / dubai gold rate / 24k-22k-21k-18k / per gram / AED.
      title = `Gold Rate in UAE Today — Dubai 24K, 22K, 21K & 18K Price Per Gram in AED`;
      description = `Live gold rate in UAE and Dubai — 24K, 22K, 21K and 18K gold price per gram, per tola and per ounce in AED, plus Abu Dhabi, Sharjah and Ajman rates. Updated every 10 minutes.`;
      locale = "en_AE";
      break;
    case "saudi":
      path = "/ar/gold-rate-saudi-arabia";
      title = `سعر الذهب في السعودية اليوم — عيار 24، 22، 21، 18`;
      description = `سعر الذهب اليوم في الرياض، جدة، مكة، المدينة والدمام. أسعار عيار 24، 22، 21 و18 لكل جرام وأوقية، ويتم تحديثها كل 10 دقائق.`;
      locale = "ar_SA";
      break;
  }

  return {
    dp,
    title,
    description,
    path,
    meta: [
      { title },
      { name: "description", content: description },
      // NOTE: no daily-changing custom meta here. Non-saudi hubs kept
      // fully stable so crawlers don't re-index them every day.
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: `${SITE_URL}${path}` },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: locale },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
  };
}

/**
 * Append-only schema stack for the 4 date-intent hubs. Returns an array
 * of JSON-LD blocks to push into route head().scripts.
 */
export function hubDateSchemas(country: CountryKey, opts: {
  liveRate24K?: number;
  currency: "PKR" | "INR" | "AED" | "SAR";
  topCity: string;
  unitLabel: string;
}) {
  const dp = getDateParts(country);
  const path =
    country === "saudi" ? "/ar/gold-rate-saudi-arabia" : `/${country}`;
  const url = `${SITE_URL}${path}`;
  const lang = country === "saudi" ? "ar" : "en";
  // Structured data stays evergreen. Live calendar dates remain visible to
  // visitors but are excluded from Google snippets with data-nosnippet.
  const dLong = "Today";
  const dNow = "every 10 minutes";
  const dSlug = "today";
  const cName =
    country === "pakistan" ? "Pakistan" :
    country === "india" ? "India" :
    country === "uae" ? "UAE" : "Saudi Arabia";

  const cities =
    country === "pakistan" ? ["Karachi","Lahore","Islamabad","Rawalpindi","Faisalabad","Multan","Peshawar","Quetta"] :
    country === "india"    ? ["Delhi","Mumbai","Bangalore","Chennai","Kolkata","Hyderabad","Ahmedabad","Pune"] :
    country === "uae"      ? ["Dubai","Abu Dhabi","Sharjah","Ajman","Al Ain","Ras Al Khaimah","Fujairah","Umm Al Quwain"] :
                             ["Riyadh","Jeddah","Mecca","Medina","Dammam","Khobar","Taif","Tabuk"];

  const webPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: country === "saudi"
      ? `سعر الذهب في السعودية اليوم`
      : country === "uae"
        ? `Gold Rate in UAE Today — Dubai 24K, 22K, 21K & 18K Per Gram`
        : `Gold Rate in ${cName} Today`,
    inLanguage: lang,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [`#today-gold-rate-${dSlug}`, "[data-speakable='today-rate']"],
    },
  };

  // Honest markup: DrHint publishes price data, it does not sell gold.
  // Product/Offer with shipping implied a merchant listing (misleading).
  const product = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: `Gold Rate — ${cName}`,
    description: `Live 24K gold rate ${opts.unitLabel} in ${cName}, recalculated every 10 minutes from international spot price and live FX.`,
    url,
    isAccessibleForFree: true,
    license: "https://creativecommons.org/licenses/by/4.0/",
    creator: { "@type": "Organization", name: "DrHint", url: `${SITE_URL}/` },
    spatialCoverage: { "@type": "Place", name: cName },
    variableMeasured: `24K gold price (${opts.currency}) ${opts.unitLabel}`,
  };

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${cName} cities — gold rate ${dLong}`,
    itemListElement: cities.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${url}#${c.toLowerCase().replace(/\s+/g, "-")}`,
      name: `${c} Gold Rate`,
    })),
  };

  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `What is the gold rate in ${cName} today?`,
        acceptedAnswer: { "@type": "Answer", text: `The live 24K gold rate in ${cName} is recalculated ${dNow} from the international XAU/USD spot price and the live ${opts.currency} exchange rate. See the per gram, ${opts.unitLabel} and per 10 gram tables above.` },
      },
      {
        "@type": "Question",
        name: `What is the 24K gold price today in ${opts.topCity}?`,
        acceptedAnswer: { "@type": "Answer", text: `The 24K gold price in ${opts.topCity} is shown live in the city section above and refreshes every 10 minutes from international spot.` },
      },
      {
        "@type": "Question",
        name: `Did the gold price go up or down today vs yesterday?`,
        acceptedAnswer: { "@type": "Answer", text: `The day-over-day price diff between yesterday and today is shown in the "Price diff today" section, updated every 10 minutes.` },
      },
      {
        "@type": "Question",
        name: `Where can I check the live gold rate in ${cName}?`,
        acceptedAnswer: { "@type": "Answer", text: `This page (${url}) shows the live ${cName} gold rate for every major city, every karat and every weight, refreshed every 10 minutes.` },
      },
      {
        "@type": "Question",
        name: `What is the gold rate ${opts.unitLabel} today?`,
        acceptedAnswer: { "@type": "Answer", text: `The ${opts.unitLabel} 24K gold rate appears in the headline tile and the full breakdown table above.` },
      },
    ],
  };

  const speakable = {
    "@context": "https://schema.org",
    "@type": "SpeakableSpecification",
    cssSelector: [`#today-gold-rate-${dSlug}`, "[data-speakable='today-rate']"],
  };

  return [webPage, product, itemList, faq, speakable];
}

export function buildCityFaqs(country: Country, cityName: string) {
  const cName = country === "pakistan" ? "Pakistan" : "India";
  const unit = country === "pakistan" ? "tola" : "10 grams";
  return [
    {
      q: `What is the difference between 22K and 24K gold in ${cityName}?`,
      a: `24K gold is 99.9% pure and used mainly for investment bars and coins. 22K gold is 91.67% pure (22 parts gold, 2 parts alloy) and is the standard for jewelry in ${cityName}, ${cName} because it is harder and more durable for daily wear.`,
    },
    {
      q: `How is today's gold rate in ${cityName} calculated?`,
      a: `The rate is derived from the international spot price (USD per troy ounce) converted to ${country === "pakistan" ? "PKR" : "INR"} at the live USD exchange rate, plus a fixed local market premium per ${unit}. It refreshes every 10 minutes.`,
    },
    {
      q: `Why does the gold price in ${cityName} change daily?`,
      a: `Gold prices change every minute on the global market, driven by USD strength, central bank demand, inflation, and geopolitical risk. The ${cName} retail rate also moves with the USD/${country === "pakistan" ? "PKR" : "INR"} exchange rate, so even a stable spot price can shift the local rate.`,
    },
  ];
}

export function faqPageSchema(faqs: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function financialProductSchema(args: {
  name: string;
  description: string;
  price: number;
  currency: "PKR" | "INR";
  url: string;
}) {
  // Honest markup: price data publication, not a merchant product listing.
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: args.name,
    description: args.description,
    url: args.url,
    isAccessibleForFree: true,
    license: "https://creativecommons.org/licenses/by/4.0/",
    creator: { "@type": "Organization", name: "DrHint", url: `${SITE_URL}/` },
    spatialCoverage: { "@type": "Place", name: args.currency === "PKR" ? "Pakistan" : "India" },
    variableMeasured: `Gold price (${args.currency}); latest ${Math.round(args.price)}`,
  };
}

export const GRAMS_PER_TOLA_CONST = GRAMS_PER_TOLA;

export function liveTimestamp(d: Date | string, tz: "PKT" | "IST" = "PKT"): string {
  const date = typeof d === "string" ? new Date(d) : d;
  const tzName = tz === "PKT" ? "Asia/Karachi" : "Asia/Kolkata";
  return (
    date.toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: tzName,
    }) + ` ${tz}`
  );
}